// pages/AssetPage.js - 资产管理
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Table, Tag, Button, Modal, Form, Input, Select, message, Row, Col } from 'antd';
import { MobileOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { api } from '../services/api';

const cardStyle = {
  background: 'linear-gradient(135deg, #0d1f44 0%, #132c5e 100%)',
  border: '1px solid rgba(0, 180, 216, 0.3)',
  borderRadius: 8,
  boxShadow: '0 0 20px rgba(0, 180, 216, 0.1)',
};
const COLORS = { primary: '#00d4ff', solar: '#f4c542', storage: '#52e8b0', text: '#fff', muted: 'rgba(255,255,255,0.6)' };

const AssetPage = () => {
  const { t } = useTranslation();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const json = await api.getDeviceList({ pageSize: 100 });
      if (json.success) setDevices(json.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchDevices(); }, []);

  const typeMap = (t) => ({ photovoltaic: t('collection.photovoltaic'), battery: t('collection.battery'), charger: t('collection.charger'), generator: t('collection.generator') });
  const statusMap = (t) => ({ 0: t('collection.offline'), 1: t('collection.running'), 2: t('collection.standby'), 3: t('collection.fault') });
  const statusColor = { 0: 'default', 1: 'green', 2: 'orange', 3: 'red' };

  const columns = [
    { title: t('asset.deviceId'), dataIndex: 'id', key: 'id', width: 70, render: val => <span style={{ color: COLORS.muted }}>{val}</span> },
    { title: t('collection.deviceName'), dataIndex: 'name', key: 'name', render: val => <span style={{ color: COLORS.text }}>{val}</span> },
    { title: t('collection.type'), dataIndex: 'type', render: val => <Tag>{typeMap(t)[val] || val}</Tag> },
    { title: t('collection.model'), dataIndex: 'model', key: 'model', render: val => <span style={{ color: COLORS.muted }}>{val || '-'}</span> },
    { title: t('collection.location'), dataIndex: 'location', key: 'location', render: val => <span style={{ color: COLORS.muted }}>{val || '-'}</span> },
    { title: t('asset.capacityUnit'), dataIndex: 'capacity', key: 'capacity', render: v => <span style={{ color: COLORS.primary }}>{v || '-'}</span> },
    { title: t('collection.status'), dataIndex: 'status', render: s => <Tag color={statusColor[s]}>{statusMap(t)[s]}</Tag> },
    { title: t('asset.operation'), key: 'action', render: (_, record) => (
      <Button type="link" style={{ color: COLORS.primary }} icon={<EditOutlined />} onClick={() => { setEditing(record); form.setFieldsValue(record); setModalVisible(true); }} />
    )},
  ];

  const totalCapacity = devices.reduce((sum, d) => sum + parseFloat(d.capacity || 0), 0);
  const onlineCount = devices.filter(d => d.status === 1).length;

  return (
    <div style={{ minHeight: '100%' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {[
          { label: t('asset.totalAssets'), value: devices.length, unit: t('collection.unit'), color: COLORS.primary },
          { label: t('collection.onlineDevices'), value: onlineCount, unit: t('collection.unit'), color: '#52c41a' },
          { label: t('asset.totalCapacity'), value: totalCapacity.toFixed(1), unit: 'kW', color: COLORS.solar },
        ].map(item => (
          <Col xs={24} sm={8} key={item.label}>
            <Card variant="borderless" style={cardStyle} styles={{ body: { padding: '20px' } }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 48, height: 48, borderRadius: 10, background: 'rgba(0,180,216,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(0,180,216,0.3)' }}>
                  <MobileOutlined style={{ fontSize: 22, color: item.color }} />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: COLORS.muted }}>{item.label}</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: item.color }}>{item.value} <span style={{ fontSize: 12, color: COLORS.muted }}>{item.unit}</span></div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card variant="borderless" style={cardStyle} styles={{ body: { padding: 20 } }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, letterSpacing: 2, paddingLeft: 12, borderLeft: '3px solid #00d4ff' }}>{t('asset.assetList')}</div>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing(null); form.resetFields(); setModalVisible(true); }}
            style={{ background: 'rgba(0,180,216,0.2)', color: COLORS.primary, border: '1px solid rgba(0,180,216,0.4)' }}>{t('asset.registerAsset')}</Button>
        </div>
        <Table columns={columns} dataSource={devices} loading={loading} rowKey="id" pagination={{ pageSize: 10 }} style={{ background: 'transparent' }} />
      </Card>

      <Modal title={editing ? t('asset.editAsset') : t('asset.registerAsset')} open={modalVisible}
        onOk={async () => {
          try {
            const values = await form.validateFields();
            const json = editing
              ? await api.updateDevice(editing.id, values)
              : await api.addDevice(values);
            if (json.success) { message.success(t('common.success')); setModalVisible(false); fetchDevices(); }
            else message.error(json.message);
          } catch (err) { message.error(t('asset.operationFailed')); }
        }}
        onCancel={() => { setModalVisible(false); form.resetFields(); }}
        okText={t('common.save')} cancelText={t('common.cancel')}
        styles={{ body: { background: '#0d1f44', color: '#fff' }, header: { background: '#0d1f44', color: '#fff' } }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label={t('collection.deviceNameLabel')} rules={[{ required: true }]}><Input /></Form.Item>
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item name="type" label={t('collection.deviceType')} rules={[{ required: true }]}>
                <Select>
                  <Select.Option value="photovoltaic">{t('collection.photovoltaic')}</Select.Option>
                  <Select.Option value="battery">{t('collection.battery')}</Select.Option>
                  <Select.Option value="charger">{t('collection.charger')}</Select.Option>
                  <Select.Option value="generator">{t('collection.generator')}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="model" label={t('collection.model')}><Input /></Form.Item>
            </Col>
          </Row>
          <Form.Item name="location" label={t('collection.location')}><Input /></Form.Item>
          <Row gutter={8}>
            <Col span={12}><Form.Item name="capacity" label={t('collection.capacity')}><Input type="number" /></Form.Item></Col>
            <Col span={12}><Form.Item name="rated_power" label={t('collection.ratedPowerKw')}><Input type="number" /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default AssetPage;
